import { Component, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { LocaleService } from '../../i18n/locale.service';
import { TranslatePipe } from '../../i18n/translate.pipe';
import { BackButton } from '../../shell/back-button/back-button';
import { type ImportErrorType, TenantImportExportService } from '../tenant-import-export.service';
import { TenantService } from '../tenant.service';

@Component({
  selector: 'cmi-tenants-list',
  imports: [RouterLink, TranslatePipe, BackButton],
  templateUrl: './list.html'
})
export class List {
  private readonly _importExportService = inject(TenantImportExportService);
  private readonly _localeService = inject(LocaleService);
  protected tenantService = inject(TenantService);
  protected deletingTenantId = signal<string | null>(null);
  protected isExporting = signal<boolean>(false);
  protected isImporting = signal<boolean>(false);
  protected importError = signal<string | null>(null);

  private readonly _errorMessages: Record<ImportErrorType, string> = {
    INVALID_JSON: this._localeService.translate('importExport.error.invalidJson'),
    NOT_AN_ARRAY: this._localeService.translate('importExport.error.notArray'),
    FILE_READ_ERROR: this._localeService.translate('importExport.error.fileRead')
  };

  protected selectTenant(id: string): void {
    this.tenantService.selectTenant(id);
    window.location.assign('/');
  }

  protected confirmDelete(id: string): void {
    this.deletingTenantId.set(id);
  }

  protected cancelDelete(): void {
    this.deletingTenantId.set(null);
  }

  protected deleteTenant(id: string): void {
    this.tenantService.deleteTenant(id);
    this.deletingTenantId.set(null);
  }

  protected exportTenants(): void {
    this.isExporting.set(true);

    const tenants = this.tenantService.getTenants();
    this._importExportService.exportToFile(tenants);
    this.isExporting.set(false);
  }

  protected async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    this.isImporting.set(true);
    this.importError.set(null);

    const result = await this._importExportService.importFromFile(file);
    if (result.error) {
      this.importError.set(this._errorMessages[result.error]);
    } else {
      result.tenants.forEach((tenant) => {
        const existingTenant = this.tenantService.getTenantByName(tenant.name);
        if (existingTenant) {
          this.tenantService.updateTenant(tenant.name, tenant);
        } else {
          this.tenantService.saveTenant(tenant);
        }
      });
    }

    this.isImporting.set(false);

    input.value = '';
  }

  protected triggerFileInput(): void {
    const fileInput = document.getElementById('file-input') as HTMLInputElement;
    fileInput?.click();
  }
}
